import React, { useEffect, useRef, useState } from 'react';
import {
    createInvoicePaymentMethodSession,
    saveInvoicePaymentMethod,
} from '../../services/paymentsService';

/**
 * AddPaymentMethod
 * ================
 *
 * Mounts the Rainforest Payment Component in *save-only* mode (i.e.
 * passing only `payment-method-config-id`, NO `payin-config-id`) so
 * that submitting the form tokenizes a card without immediately
 * charging it. When the widget fires `approved` we POST the resulting
 * `payment_method_id` back to our server, which re-fetches the
 * authoritative payment_method from Rainforest, validates the metadata
 * against the gate token, and writes the vault doc.
 *
 * Two important UX details:
 *   - Rainforest's `<rainforest-payment>` element gets recreated on
 *     every session change, so we drive the embed via React keying on
 *     the session_key; this also clears widget state if the patient
 *     hits "Cancel" and re-opens.
 *   - We don't trust the widget's `approved` payload for anything other
 *     than the `payment_method_id`. The server fetches everything else
 *     it needs straight from Rainforest, so a tampered client cannot
 *     forge metadata or claim ownership of an unrelated card.
 *
 * Props:
 *   publicHash, gateToken — auth context for the public endpoints
 *   makeDefault           — checked state of the "Make this my default" UI
 *   setMakeDefault
 *   hasExistingCards      — when false, the "make default" checkbox is
 *                           force-checked and disabled. The vault auto-
 *                           promotes the only ACTIVE card to default, so
 *                           offering an unchecked toggle here would be
 *                           misleading.
 *   onSaved(savedRow)     — bubbled up so the parent can refetch / close
 *   onCancel()
 */
export default function AddPaymentMethod({
    publicHash,
    gateToken,
    makeDefault,
    setMakeDefault,
    hasExistingCards = true,
    onSaved,
    onCancel,
}) {
    const [sessionKey, setSessionKey] = useState(null);
    const [pmcId, setPmcId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const componentRef = useRef(null);

    // First card on a wallet is *always* the default — the vault has
    // nothing to fall back to. Force the parent's makeDefault state to
    // true while the wallet is empty so the request body matches what
    // the user sees on screen (and what the vault will end up doing
    // either way).
    useEffect(() => {
        if (!hasExistingCards) setMakeDefault?.(true);
    }, [hasExistingCards, setMakeDefault]);

    // Mint the save-only session as soon as we mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            const { ok, data } = await createInvoicePaymentMethodSession(publicHash, gateToken);
            if (cancelled) return;
            if (!ok || !data?.success) {
                setError(data?.error || 'Could not load the card form.');
                setLoading(false);
                return;
            }
            setSessionKey(data.session_key);
            setPmcId(data.payment_method_config_id);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [publicHash, gateToken]);

    // Wire the Rainforest event handlers once the element mounts.
    useEffect(() => {
        if (!sessionKey || !componentRef.current) return undefined;
        const el = componentRef.current;

        const handleApproved = async (event) => {
            const rawDetail = event?.detail;
            console.log('[AddPaymentMethod] approved detail', rawDetail);
            // Rainforest dispatches event.detail as a one-element array
            // wrapping the API-style envelope:
            //   [{ status: 'SUCCESS', data: { payment_method_id: 'mtd_...', ... }, errors: null }]
            // Older docs/SDKs sometimes return the envelope directly, so
            // we normalise both shapes before extracting.
            const envelope = Array.isArray(rawDetail) ? rawDetail[0] : rawDetail;
            const data = (envelope && envelope.data) || envelope || {};
            const paymentMethodId =
                data.payment_method_id ||
                data.paymentMethodId ||
                data.payment_method?.id ||
                data.id ||
                envelope?.payment_method_id ||
                envelope?.id;
            if (!paymentMethodId) {
                setError('The card form did not return a payment method id.');
                return;
            }
            setSubmitting(true);
            setError(null);
            try {
                const { ok, data } = await saveInvoicePaymentMethod(publicHash, gateToken, {
                    paymentMethodId,
                    makeDefault: !!makeDefault,
                });
                if (!ok || !data?.success) {
                    setError(data?.error || 'Could not save this card.');
                    return;
                }
                onSaved?.(data.paymentMethod);
            } finally {
                setSubmitting(false);
            }
        };
        const handleDeclined = (event) => {
            console.log('[AddPaymentMethod] declined', event);
            setError('The card was declined. Please try a different card.');
        };
        const handleError = (event) => {
            console.log('[AddPaymentMethod] error', event);
            setError('There was a problem with the card form. Please try again.');
        };

        el.addEventListener('approved', handleApproved);
        el.addEventListener('declined', handleDeclined);
        el.addEventListener('error', handleError);

        // Rainforest's save-only tokenization mode fires either `approved`
        // (newer SDKs) or `payment-method` / `tokenized` (older ones). Wire
        // them all up so we don't quietly miss the success signal.
        el.addEventListener('payment-method', handleApproved);
        el.addEventListener('payment_method', handleApproved);
        el.addEventListener('tokenized', handleApproved);

        // Dev-only: log everything so we can tell which event the widget
        // actually dispatches if the save flow falls through.
        const KNOWN_EVENTS = [
            'loaded', 'submit', 'approved', 'declined', 'error',
            'attempted', 'payment-method', 'payment_method', 'tokenized',
            'change', 'ready',
        ];
        const debugListeners = KNOWN_EVENTS.map((name) => {
            const fn = (e) => console.log(`[AddPaymentMethod] event=${name}`, e?.detail);
            el.addEventListener(name, fn);
            return [name, fn];
        });

        return () => {
            el.removeEventListener('approved', handleApproved);
            el.removeEventListener('declined', handleDeclined);
            el.removeEventListener('error', handleError);
            el.removeEventListener('payment-method', handleApproved);
            el.removeEventListener('payment_method', handleApproved);
            el.removeEventListener('tokenized', handleApproved);
            debugListeners.forEach(([name, fn]) => el.removeEventListener(name, fn));
        };
    }, [sessionKey, publicHash, gateToken, makeDefault, onSaved]);

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e6ed',
            borderRadius: 8,
            padding: 'clamp(16px, 4vw, 24px)',
            marginBottom: 16,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Add a card</div>
                <button type="button" onClick={onCancel} style={ghostButton}>Cancel</button>
            </div>

            {loading && (
                <div style={{ color: '#586473', fontSize: 13 }}>Loading secure card form…</div>
            )}

            {!loading && sessionKey && pmcId && (
                <>
                    {/* The element is keyed on sessionKey so a fresh session
                        always remounts a clean iframe (React would otherwise
                        reuse the existing element with stale internal state). */}
                    <rainforest-payment
                        key={sessionKey}
                        ref={componentRef}
                        session-key={sessionKey}
                        payment-method-config-id={pmcId}
                        allowed-methods="CARD"
                    ></rainforest-payment>

                    <label
                        title={hasExistingCards
                            ? ''
                            : 'First card on file is always the default'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 12,
                            fontSize: 13,
                            color: hasExistingCards ? '#586473' : '#9aa4b1',
                            cursor: hasExistingCards ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={!!makeDefault}
                            disabled={!hasExistingCards}
                            onChange={(e) => setMakeDefault?.(e.target.checked)}
                            style={{ cursor: hasExistingCards ? 'pointer' : 'not-allowed' }}
                        />
                        Make this my default payment method
                        {!hasExistingCards && (
                            <span style={{
                                fontSize: 11,
                                color: '#9aa4b1',
                                fontStyle: 'italic',
                            }}>
                                (first card is always default)
                            </span>
                        )}
                    </label>
                </>
            )}

            {submitting && (
                <div style={{ color: '#586473', fontSize: 13, marginTop: 8 }}>
                    Saving card…
                </div>
            )}

            {error && (
                <div style={{ color: '#b3261e', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}
        </div>
    );
}

const ghostButton = {
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#586473',
    border: '1px solid #c4ccd6',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
};
